/**
 * Übersetzer Bausteine → Vorlagen-HTML (Schritt 2 des Vertragsbaukastens). Ergebnis ist genau das Markup, das heute in
 * contract_templates steht: body_html, annex_html, annex_hidden_from, variables und required_signers. Der Renderer
 * (contract-render.ts), die Prüfung (contract-check.ts), der Versand (contract_send), Unterschrift, Sicht je Partei und die
 * Beweiskette bleiben unverändert.
 *
 * Regeln:
 * - Jeder Text wird escaped (&, <, >) und jedes „{“ im Text als &#123; geschrieben: Aus getipptem Text wird nie ein
 *   Platzhalter und nie Markup. Platzhalter entstehen nur aus Feld-Chips, Bedingungen und Unterschriften.
 * - Es entsteht nur Markup der Positivliste (contract-markup.ts); legacy_html bleibt, wie es ist (das prüft der Server).
 * - Auszeichnungen (fett, kursiv, unterstrichen) werden über benachbarte Läufe zusammengefasst; vor einem „Nur wenn“ im Satz
 *   werden sie geschlossen.
 * - Der vertrauliche Teil (Stufe 1) wird zu annex_html; annex_hidden_from sind die Rollen außerhalb von visibleTo.
 * - Wählbare Klauseln ergeben {{#if var.key}}…{{/if}} und eine Ja/Nein-Angabe mit ui = "choice".
 * - required_signers sind alle Rollen mit Unterschriftsfeld (Reihenfolge agency, brand, talent).
 */
import {
  DEFAULT_WS, defaultWs, fieldPath,
  type Block, type ContainerKind, type FieldRef, type Inline, type InlineMark, type ListItem,
} from "./contract-blocks.js";
import { CONTRACT_ROLES, type ContractRole, type CustomVariableDef } from "./contract-variables.js";
import type { Language } from "./types.js";

export interface CompileOptions {
  /** Sprache der Vorlage (Beschriftung „Anlage 1“ / „Annex 1“) */
  language: Language;
  /** eigene Angaben der Vorlage; Wahlklauseln werden ergänzt bzw. aktualisiert */
  variables?: readonly CustomVariableDef[];
}

export interface CompiledTemplate {
  bodyHtml: string;
  annexHtml: string | null;
  annexHiddenFrom: ContractRole[];
  variables: CustomVariableDef[];
  requiredSigners: ContractRole[];
}

/** Eigene Angabe einer wählbaren Klausel (CustomVariableDef mit Zusatzfeldern für den Assistenten). */
export interface ChoiceVariableDef extends CustomVariableDef {
  ui: "choice";
  help?: { de: string; en: string };
}

/** Text escapen: &, <, > und jedes „{“ (nie ein Platzhalter aus Text). Anführungszeichen bleiben, wie sie sind. */
export function escapeContractText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\{/g, "&#123;");
}

const MARK_TAG: Record<InlineMark, string> = { b: "strong", i: "em", u: "u" };

function placeholder(f: FieldRef, filters?: readonly string[]): string {
  return `{{${fieldPath(f)}${(filters ?? []).map((x) => `|${x}`).join("")}}}`;
}

/** Inline-Inhalt in HTML; Auszeichnungen minimal geschachtelt, vor „Nur wenn“ geschlossen. */
export function compileInline(nodes: readonly Inline[]): string {
  let out = "";
  const stack: InlineMark[] = [];
  const openTo = (marks: readonly InlineMark[]) => {
    let k = 0;
    while (k < stack.length && k < marks.length && stack[k] === marks[k]) k += 1;
    while (stack.length > k) out += `</${MARK_TAG[stack.pop()!]}>`;
    for (let j = k; j < marks.length; j++) {
      out += `<${MARK_TAG[marks[j]!]}>`;
      stack.push(marks[j]!);
    }
  };
  for (const n of nodes) {
    if (n.t === "if") {
      openTo([]);
      out += `{{#if ${fieldPath(n.when)}}}${compileInline(n.then)}${n.else !== undefined ? `{{else}}${compileInline(n.else)}` : ""}{{/if}}`;
      continue;
    }
    openTo(n.marks ?? []);
    if (n.t === "text") out += escapeContractText(n.v);
    else if (n.t === "br") out += "<br>";
    else out += placeholder(n.field, n.filters);
  }
  openTo([]);
  return out;
}

interface Ctx {
  language: Language;
  attachments: number;
}

function compileList(blocks: readonly Block[], kind: ContainerKind, ctx: Ctx): string {
  let out = "";
  blocks.forEach((b, i) => {
    out += (b.ws ?? defaultWs(kind, i)) + compileBlock(b, ctx);
  });
  return out;
}

function listItem(it: ListItem): string {
  const li = `<li>${compileInline(it.text)}</li>`;
  return (it.ws ?? DEFAULT_WS.item) + (it.when ? `{{#if ${fieldPath(it.when)}}}${li}{{/if}}` : li);
}

/**
 * Ein Baustein als HTML (ohne den Leerraum davor). Der vertrauliche Teil ergibt hier seinen Inhalt (annex_html); im
 * Hauptteil wird er übersprungen (compileBlocks).
 */
export function compileBlock(b: Block, ctx: Ctx = { language: "de", attachments: 0 }): string {
  const align = (a: "center" | "right" | undefined) => (a ? ` class="tk-align-${a}"` : "");
  switch (b.type) {
    case "heading":
      return `<h${b.level}${align(b.align)}>${compileInline(b.text)}</h${b.level}>`;
    case "paragraph":
      return `<p${align(b.align)}>${compileInline(b.text)}</p>`;
    case "note":
      return `<p class="tk-sample-notice">${compileInline(b.text)}</p>`;
    case "list": {
      const tag = b.ordered ? "ol" : "ul";
      return `<${tag}>${b.items.map(listItem).join("")}${b.wsEnd ?? DEFAULT_WS.listEnd}</${tag}>`;
    }
    case "table": {
      const head = b.header ? `<thead><tr>${b.header.map((c) => `<th>${compileInline(c)}</th>`).join("")}</tr></thead>` : "";
      const body = `<tbody>${b.rows.map((r) => `<tr>${r.map((c) => `<td>${compileInline(c)}</td>`).join("")}</tr>`).join("")}</tbody>`;
      return `<table>${head}${body}</table>`;
    }
    case "field":
      return `<p><strong>${escapeContractText(b.label)}:</strong> ${placeholder(b.field, b.filters)}</p>`;
    case "condition": {
      const then = compileList(b.children, "cond", ctx);
      if (b.otherwise === undefined) return `{{#if ${fieldPath(b.when)}}}${then}${b.wsEnd ?? DEFAULT_WS.end.cond}{{/if}}`;
      return `{{#if ${fieldPath(b.when)}}}${then}${b.wsElse ?? DEFAULT_WS.else}{{else}}${compileList(b.otherwise, "cond", ctx)}${b.wsEnd ?? DEFAULT_WS.end.cond}{{/if}}`;
    }
    case "choice":
      return `{{#if var.${b.key}}}${compileList(b.children, "cond", ctx)}${b.wsEnd ?? DEFAULT_WS.end.cond}{{/if}}`;
    case "private":
      return `${compileList(b.children, "annex", ctx)}${b.wsEnd ?? DEFAULT_WS.end.annex}`;
    case "attachment": {
      ctx.attachments += 1;
      const label = `${ctx.language === "en" ? "Annex" : "Anlage"} ${ctx.attachments}: ${b.title}`;
      return `<h2>${escapeContractText(label)}</h2>${compileList(b.children, "attachment", ctx)}${b.wsEnd ?? DEFAULT_WS.end.attachment}`;
    }
    case "clause":
      return compileList(b.children, "clause", ctx);
    case "signatures":
      return b.parties.map((r) => `{{signature:${r}}}`).join(b.sep ?? DEFAULT_WS.sep);
    case "pagebreak":
      return `<div class="tk-page-break"></div>`;
    case "legacy_html":
      return b.html;
  }
}

function collect(blocks: readonly Block[], out: { roles: Set<ContractRole>; choices: Map<string, ChoiceVariableDef> }): void {
  for (const b of blocks) {
    if (b.type === "signatures") for (const r of b.parties) out.roles.add(r);
    if (b.type === "choice") {
      out.choices.set(b.key, {
        key: b.key, label: { de: b.question.de, en: b.question.en }, type: "boolean", required: false, default: b.default, ui: "choice",
        ...(b.help ? { help: { de: b.help.de, en: b.help.en } } : {}),
      });
    }
    if (b.type === "condition") { collect(b.children, out); if (b.otherwise) collect(b.otherwise, out); }
    if (b.type === "choice" || b.type === "private" || b.type === "attachment" || b.type === "clause") collect(b.children, out);
  }
}

/**
 * Bausteine einer Vorlage in die heutigen Vorlagenfelder übersetzen. Der Hauptteil sind alle Bausteine außer dem vertraulichen
 * Teil (Leerraum-Standard je Stelle), der vertrauliche Teil wird annex_html.
 */
export function compileBlocks(blocks: readonly Block[], opts: CompileOptions): CompiledTemplate {
  const ctx: Ctx = { language: opts.language, attachments: 0 };
  const body = blocks.filter((b) => b.type !== "private");
  const priv = blocks.find((b) => b.type === "private");
  const bodyHtml = compileList(body, "body", ctx);
  const annexHtml = priv ? compileBlock(priv, ctx) : null;
  const annexHiddenFrom = priv && priv.type === "private" ? CONTRACT_ROLES.filter((r) => !priv.visibleTo.includes(r)) : [];
  const found = { roles: new Set<ContractRole>(), choices: new Map<string, ChoiceVariableDef>() };
  collect(blocks, found);
  const variables: CustomVariableDef[] = (opts.variables ?? []).map((v) => found.choices.get(v.key) ?? v);
  for (const [key, def] of found.choices) if (!variables.some((v) => v.key === key)) variables.push(def);
  return { bodyHtml, annexHtml, annexHiddenFrom, variables, requiredSigners: CONTRACT_ROLES.filter((r) => found.roles.has(r)) };
}
